import clsx from 'clsx';
import Button from 'components/common/Button';
import ErrorMessage from 'components/enter2026late/ErrorMessage';
import SlidingModal, {
  Props as SlidingModalProps,
} from 'components/enter2026late/SlidingModal';
import { useUserStore } from 'hooks/stores/useUserStore';
import { useEffect, useId, useState } from 'react';
import { tryApi } from 'utils/api/common';
import {
  getPlayerUpdateDetailsErrorMessage,
  playerUpdateDetailsErrorMessageFallback,
} from 'utils/api/playerApi';
import { playerApiClient } from 'utils/apiClient';
import HttpStatusCode from 'utils/HttpStatusCode';

import styles from './ChangeEmailAddressSlidingModal.module.css';

interface UseChangeEmailAddressSlidingModalOptions {
  slideAmount?: SlidingModalProps['slideAmount'];
}

export const useChangeEmailAddressSlidingModal = (
  options: UseChangeEmailAddressSlidingModalOptions,
) => {
  const [isOpen, setIsOpen] = useState(false);
  const [emailAddress, setEmailAddress] = useState('');

  return {
    render: () => (
      <ChangeEmailAddressSlidingModal
        emailAddress={emailAddress}
        onClose={() => setIsOpen(false)}
        isOpen={isOpen}
        slideAmount={options.slideAmount}
      />
    ),
    startEditing: (emailAddress: string) => {
      setEmailAddress(emailAddress);
      setIsOpen(true);
    },
  };
};

interface Props extends SlidingModalProps {
  emailAddress: string;
}

const ChangeEmailAddressSlidingModal = (props: Props) => {
  const userStore = useUserStore();
  const [loading, setLoading] = useState(false);
  const [emailAddress, setEmailAddress] = useState(props.emailAddress);
  const [errorMessage, setErrorMessage] = useState('');
  const textBoxId = useId();
  useEffect(() => {
    setEmailAddress(props.emailAddress);
    setLoading(false);
    setErrorMessage('');
  }, [props.emailAddress]);
  return (
    <SlidingModal
      isOpen={props.isOpen}
      onClose={props.onClose}
      background="darkBlue"
      slideAmount={props.slideAmount}
    >
      <div
        className={clsx(
          'daymadeTypographySRegular',
          'withReducedSpacing',
          styles.container,
        )}
      >
        <h3 className={clsx('daymadeTypographyMBlack', styles.heading)}>
          Change your email
        </h3>
        <div className={styles.formContainer}>
          <p>
            Enter your best email. It's where we'll tell you if you have won and
            share your membership details
          </p>
          {errorMessage && (
            <ErrorMessage background="onDark">
              <span className={styles.errorIntro}>Whoops...</span>{' '}
              {errorMessage}
            </ErrorMessage>
          )}
          <div>
            <label className="daymadeTypographySMedium" htmlFor={textBoxId}>
              New email address
            </label>
            <input
              id={textBoxId}
              className="dm2026textBox dm2026textBoxOnDark"
              type="email"
              placeholder="name@example.com"
              autoComplete="email"
              autoCorrect="off"
              value={emailAddress}
              onChange={(e) => setEmailAddress(e.currentTarget.value)}
            />
          </div>
          <Button
            buttonStyle="Primary"
            fullWidth
            buttonSize="Small"
            loading={loading}
            onClick={async () => {
              if (emailAddress === props.emailAddress) {
                props.onClose();
                return;
              }

              setLoading(true);
              const response = await tryApi(
                playerApiClient.auth.updateDetails({
                  body: { player: { emailAddress: emailAddress } },
                }),
              );
              if (response.status === HttpStatusCode.OK) {
                await userStore.updateCurrentUser();
                setLoading(false);
                props.onClose();
              } else if (response.status === HttpStatusCode.BAD_REQUEST) {
                const knownError = getPlayerUpdateDetailsErrorMessage(response);
                setErrorMessage(
                  knownError || playerUpdateDetailsErrorMessageFallback,
                );
                setLoading(false);
                if (!knownError) {
                  // If we get an error that we don't know how to handle, it might be fatal. The sliding modal only has a "save" button, so lets close the modal to prevent the user getting stuck
                  props.onClose();
                }
              } else if (response.status === 'error') {
                // If we get an error that we don't know how to handle, it might be fatal. The sliding modal only has a "save" button, so lets close the modal to prevent the user getting stuck
                props.onClose();
                setLoading(false);

                throw new Error(
                  'ChangeEmailAddressSlidingModal received error when updating player details',
                  { cause: response.error },
                );
              }
            }}
          >
            Update Email
          </Button>
        </div>
      </div>
    </SlidingModal>
  );
};

export default ChangeEmailAddressSlidingModal;
